/**
 * Inference Job Detail Page
 *
 * Shows job status, allows file upload, and displays detection results.
 */

import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Chip,
  Modal,
  ModalContent,
  Progress,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from '@heroui/react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  AlertCircle,
  ArrowLeft,
  AudioWaveform,
  CheckCircle,
  Clock,
  FileAudio,
  Loader2,
  Settings,
  Upload,
  XCircle,
  Zap,
} from 'lucide-react';
import { useCallback, useState } from 'react';

import { useModel } from '@/lib/api/hooks/models';
import {
  useInferenceJob,
  useInferenceFiles,
  useGetInferenceUploadUrl,
  useConfirmInferenceUpload,
} from '@/lib/api/hooks/inference';
import type { Detection, InferenceFileResponse } from '@/api';
import { DetectionViewer } from '@/components/features/inference';

interface UploadFile {
  id: string;
  file: File;
  status: 'pending' | 'uploading' | 'confirming' | 'done' | 'error';
  progress: number;
  error?: string;
}

export const Route = createFileRoute('/inference/$jobId')({
  component: InferenceJobDetailPage,
});

function InferenceJobDetailPage() {
  const { jobId } = Route.useParams();
  const navigate = useNavigate();

  const { data: job, isLoading: isLoadingJob } = useInferenceJob(jobId);
  const { data: files, isLoading: isLoadingFiles } = useInferenceFiles(jobId);
  const { data: model } = useModel(job?.model_id || '');
  const getUploadUrl = useGetInferenceUploadUrl(jobId);
  const confirmUpload = useConfirmInferenceUpload(jobId);

  const [uploadFiles, setUploadFiles] = useState<UploadFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<InferenceFileResponse | null>(null);

  const updateUploadFile = (id: string, updates: Partial<UploadFile>) => {
    setUploadFiles((prev) =>
      prev.map((f) => (f.id === id ? { ...f, ...updates } : f))
    );
  };

  const uploadFile = useCallback(async (uploadFile: UploadFile) => {
    try {
      updateUploadFile(uploadFile.id, { status: 'uploading', progress: 0 });

      // Get presigned URL
      const urlData = await getUploadUrl.mutateAsync({
        filename: uploadFile.file.name,
        content_type: uploadFile.file.type || 'audio/mpeg',
        file_size_bytes: uploadFile.file.size,
      });

      if (!urlData) {
        throw new Error('Failed to get upload URL');
      }

      updateUploadFile(uploadFile.id, { progress: 30 });

      // Upload to S3
      const uploadResponse = await fetch(urlData.upload_url, {
        method: 'PUT',
        body: uploadFile.file,
        headers: {
          'Content-Type': uploadFile.file.type || 'audio/mpeg',
        },
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload file');
      }

      updateUploadFile(uploadFile.id, { status: 'confirming', progress: 70 });

      // Confirm upload
      await confirmUpload.mutateAsync(urlData.file_id);

      updateUploadFile(uploadFile.id, { status: 'done', progress: 100 });
    } catch (error) {
      updateUploadFile(uploadFile.id, {
        status: 'error',
        error: error instanceof Error ? error.message : 'Upload failed',
      });
    }
  }, [getUploadUrl, confirmUpload]);

  const handleFiles = useCallback((fileList: FileList) => {
    const audioFiles = Array.from(fileList).filter(
      (file) =>
        file.type.startsWith('audio/') ||
        file.name.match(/\.(wav|mp3|ogg|flac|m4a)$/i)
    );

    const newFiles: UploadFile[] = audioFiles.map((file) => ({
      id: `${file.name}-${Date.now()}-${Math.random()}`,
      file,
      status: 'pending',
      progress: 0,
    }));

    setUploadFiles((prev) => [...prev, ...newFiles]);
    newFiles.forEach((f) => uploadFile(f));
  }, [uploadFile]);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      handleFiles(e.dataTransfer.files);
    },
    [handleFiles]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        handleFiles(e.target.files);
      }
    },
    [handleFiles]
  );

  if (isLoadingJob) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!job) {
    return (
      <div className="container-page py-6">
        <Card className="border border-default-100 shadow-none">
          <CardBody className="flex flex-col items-center justify-center py-12">
            <AlertCircle className="h-12 w-12 text-danger mb-4" />
            <p className="text-lg font-medium">Job not found</p>
            <Button
              variant="flat"
              size="sm"
              className="mt-4"
              onPress={() => navigate({ to: '/inference' })}
            >
              Back to Inference Jobs
            </Button>
          </CardBody>
        </Card>
      </div>
    );
  }

  const config = job.config as {
    threshold?: number;
    merge_window_ms?: number;
    min_duration_ms?: number;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'processing':
      case 'pending':
        return 'primary';
      case 'failed':
        return 'danger';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4" />;
      case 'processing':
        return <Loader2 className="h-4 w-4 animate-spin" />;
      case 'pending':
        return <Clock className="h-4 w-4" />;
      case 'failed':
        return <XCircle className="h-4 w-4" />;
      default:
        return null;
    }
  };

  const formatDuration = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const completedUploadCount = uploadFiles.filter((f) => f.status === 'done').length;
  const isUploading = uploadFiles.some((f) =>
    ['pending', 'uploading', 'confirming'].includes(f.status)
  );

  return (
    <div className="container-page py-6">
      {/* Header */}
      <Button
        variant="light"
        size="sm"
        startContent={<ArrowLeft className="h-4 w-4" />}
        onPress={() => navigate({ to: '/inference' })}
        className="mb-4"
      >
        Back to Inference Jobs
      </Button>

      <div className="mb-6 flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-2xl font-bold">Inference Job</h1>
            <Chip
              color={getStatusColor(job.status)}
              variant="flat"
              size="sm"
              startContent={getStatusIcon(job.status)}
            >
              {job.status.charAt(0).toUpperCase() + job.status.slice(1)}
            </Chip>
          </div>
          <p className="text-sm text-default-500 font-mono">{job.id}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        {/* Job Stats */}
        <Card className="border border-default-100 shadow-none">
          <CardBody className="flex flex-row items-center gap-4 py-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
              <FileAudio className="h-6 w-6 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-bold">{job.total_files}</p>
              <p className="text-xs text-default-500">Total Files</p>
            </div>
          </CardBody>
        </Card>

        <Card className="border border-default-100 shadow-none">
          <CardBody className="flex flex-row items-center gap-4 py-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-success/10">
              <CheckCircle className="h-6 w-6 text-success" />
            </div>
            <div>
              <p className="text-2xl font-bold">{job.processed_files}</p>
              <p className="text-xs text-default-500">Processed</p>
            </div>
          </CardBody>
        </Card>

        <Card className="border border-default-100 shadow-none">
          <CardBody className="flex flex-row items-center gap-4 py-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-warning/10">
              <Zap className="h-6 w-6 text-warning" />
            </div>
            <div>
              <p className="text-2xl font-bold">{job.total_detections}</p>
              <p className="text-xs text-default-500">Detections</p>
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Model & Configuration */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <Card className="border border-default-100 shadow-none">
          <CardHeader className="gap-3 pb-1 pt-3 px-4">
            <Zap className="h-4 w-4 text-default-500" />
            <h3 className="text-sm font-medium">Model</h3>
          </CardHeader>
          <CardBody className="px-4 pb-4 pt-2">
            {model ? (
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                  <Zap className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium">
                    {model.name || `Model ${model.id.slice(0, 8)}`}
                  </p>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {model.artifact_types.map((type) => (
                      <span
                        key={type}
                        className="inline-flex items-center rounded-full bg-default-100 px-2 py-0.5 text-xs text-default-600"
                      >
                        {type}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-sm text-default-500">Loading...</p>
            )}
          </CardBody>
        </Card>

        <Card className="border border-default-100 shadow-none">
          <CardHeader className="gap-3 pb-1 pt-3 px-4">
            <Settings className="h-4 w-4 text-default-500" />
            <h3 className="text-sm font-medium">Configuration</h3>
          </CardHeader>
          <CardBody className="px-4 pb-4 pt-2">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-default-500">Threshold</span>
                <span className="font-mono">{((config.threshold ?? 0.5) * 100).toFixed(0)}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-default-500">Merge Window</span>
                <span className="font-mono">{config.merge_window_ms ?? 500}ms</span>
              </div>
              <div className="flex justify-between">
                <span className="text-default-500">Min Duration</span>
                <span className="font-mono">{config.min_duration_ms ?? 100}ms</span>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>

      {/* File Upload */}
      <Card className="border border-default-100 shadow-none mb-6">
        <CardHeader className="gap-3 pb-1 pt-3 px-4">
          <Upload className="h-4 w-4 text-default-500" />
          <h3 className="text-sm font-medium">Upload Audio Files</h3>
        </CardHeader>
        <CardBody className="px-4 pb-4 pt-2">
          {/* Drop zone */}
          <div
            className={`mb-4 flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 transition-colors ${
              isDragging
                ? 'border-primary bg-primary/5'
                : 'border-default-200 hover:border-default-300'
            }`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            <div className="rounded-full bg-default-100 p-3 mb-3">
              <Upload className="h-6 w-6 text-default-400" />
            </div>
            <p className="text-sm font-medium mb-1">Drop audio files here</p>
            <p className="text-xs text-default-500 mb-3">or click to browse</p>
            <input
              type="file"
              accept="audio/*,.wav,.mp3,.ogg,.flac,.m4a"
              multiple
              onChange={handleFileInput}
              className="hidden"
              id="inference-file-input"
            />
            <Button
              as="label"
              htmlFor="inference-file-input"
              variant="flat"
              size="sm"
              className="cursor-pointer"
            >
              Browse Files
            </Button>
          </div>

          {/* Upload progress */}
          {uploadFiles.length > 0 && (
            <div className="space-y-2 max-h-40 overflow-y-auto">
              <div className="flex items-center justify-between text-xs text-default-500 mb-2">
                <span>
                  Uploading ({completedUploadCount}/{uploadFiles.length})
                </span>
                {isUploading && <Spinner size="sm" />}
              </div>
              {uploadFiles.map((file) => (
                <div
                  key={file.id}
                  className="flex items-center gap-3 rounded-lg border border-default-100 p-2"
                >
                  <FileAudio className="h-4 w-4 text-default-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="truncate text-xs font-medium">{file.file.name}</p>
                    {file.status === 'error' ? (
                      <p className="text-xs text-danger">{file.error}</p>
                    ) : (
                      <Progress
                        size="sm"
                        value={file.progress}
                        color={file.status === 'done' ? 'success' : 'primary'}
                        className="mt-1"
                      />
                    )}
                  </div>
                  <div className="flex-shrink-0">
                    {file.status === 'done' && (
                      <CheckCircle className="h-4 w-4 text-success" />
                    )}
                    {file.status === 'error' && (
                      <XCircle className="h-4 w-4 text-danger" />
                    )}
                    {['pending', 'uploading', 'confirming'].includes(file.status) && (
                      <Loader2 className="h-4 w-4 animate-spin text-primary" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      {/* Files & Results */}
      <Card className="border border-default-100 shadow-none">
        <CardHeader className="gap-3 pb-1 pt-3 px-4">
          <FileAudio className="h-4 w-4 text-default-500" />
          <h3 className="text-sm font-medium">Files & Results</h3>
        </CardHeader>
        <CardBody className="px-4 pb-4 pt-2">
          {isLoadingFiles ? (
            <div className="flex items-center justify-center py-8">
              <Spinner size="sm" />
            </div>
          ) : files?.items && files.items.length > 0 ? (
            <Table
              aria-label="Inference files"
              removeWrapper
              classNames={{
                th: 'text-xs font-medium text-default-500 bg-default-50',
                td: 'text-xs py-3',
              }}
            >
              <TableHeader>
                <TableColumn>FILE</TableColumn>
                <TableColumn>DURATION</TableColumn>
                <TableColumn>STATUS</TableColumn>
                <TableColumn>DETECTIONS</TableColumn>
                <TableColumn width={120}>ACTIONS</TableColumn>
              </TableHeader>
              <TableBody>
                {files.items.map((file) => (
                  <TableRow key={file.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <FileAudio className="h-4 w-4 text-default-400" />
                        <span className="font-medium">{file.original_filename}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {file.duration_ms ? formatDuration(file.duration_ms) : '—'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        color={getStatusColor(file.status)}
                        variant="flat"
                        size="sm"
                        startContent={getStatusIcon(file.status)}
                      >
                        {file.status}
                      </Chip>
                    </TableCell>
                    <TableCell>
                      {file.status === 'completed' ? (
                        <span className="font-medium">{file.detection_count}</span>
                      ) : file.status === 'failed' ? (
                        <span className="text-danger">{file.error_message || 'Error'}</span>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>
                      {file.status === 'completed' && (
                        <Button
                          size="sm"
                          variant="flat"
                          startContent={<AudioWaveform className="h-3.5 w-3.5" />}
                          onPress={() => setSelectedFile(file)}
                        >
                          View
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="rounded-full bg-default-100 p-4 mb-4">
                <FileAudio className="h-8 w-8 text-default-400" />
              </div>
              <p className="text-sm font-medium mb-1">No files uploaded yet</p>
              <p className="text-xs text-default-500">
                Upload audio files above to run artifact detection
              </p>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Detection Results - shown for files with detections */}
      {files?.items && files.items.some((f) => f.detections && f.detections.length > 0) && (
        <Card className="border border-default-100 shadow-none mt-4">
          <CardHeader className="gap-3 pb-1 pt-3 px-4">
            <Zap className="h-4 w-4 text-default-500" />
            <h3 className="text-sm font-medium">Detection Details</h3>
          </CardHeader>
          <CardBody className="px-4 pb-4 pt-2">
            <div className="space-y-4">
              {files.items
                .filter((f) => f.detections && f.detections.length > 0)
                .map((file) => (
                  <div key={file.id} className="rounded-lg border border-default-100 p-3">
                    <div className="flex items-center gap-2 mb-3">
                      <FileAudio className="h-4 w-4 text-default-400" />
                      <span className="text-sm font-medium">{file.original_filename}</span>
                      <Chip size="sm" variant="flat">
                        {file.detection_count} detections
                      </Chip>
                      <Button
                        size="sm"
                        variant="flat"
                        startContent={<AudioWaveform className="h-3.5 w-3.5" />}
                        onPress={() => setSelectedFile(file)}
                        className="ml-auto"
                      >
                        View Waveform
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {file.detections?.map((detection: Detection, idx: number) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between rounded bg-default-50 px-3 py-2 text-xs"
                        >
                          <div className="flex items-center gap-3">
                            <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-primary">
                              {detection.artifact_type}
                            </span>
                            <span className="font-mono text-default-500">
                              {formatDuration(detection.start_ms)} -{' '}
                              {formatDuration(detection.end_ms)}
                            </span>
                          </div>
                          <span className="font-mono">
                            {(detection.confidence * 100).toFixed(0)}%
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
            </div>
          </CardBody>
        </Card>
      )}

      {/* Detection Viewer Modal */}
      <Modal
        isOpen={!!selectedFile}
        onClose={() => setSelectedFile(null)}
        size="full"
        hideCloseButton
        classNames={{
          base: 'max-h-screen',
          body: 'p-0',
        }}
      >
        <ModalContent>
          {selectedFile && (
            <DetectionViewer
              jobId={jobId}
              fileId={selectedFile.id}
              artifactTypes={
                model?.artifact_types.map((type) => ({
                  name: type,
                  color: '', // Let DetectionViewer use default colors
                })) ?? []
              }
              onClose={() => setSelectedFile(null)}
            />
          )}
        </ModalContent>
      </Modal>
    </div>
  );
}
